import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const HERO_BG = "/brand/product/open-display.jpg";
const MASCOT = "/brand/mascot.png";
const LOGO = "/brand/logo.jpeg";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMascot = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = () => document.querySelector("#story")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden flex items-center">
      {/* Background flames */}
      <div className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-40 mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
        <div className="ember-glow absolute inset-0 animate-flicker" />
      </div>

      {/* Giant outlined watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span className="font-display text-outline text-[38vw] leading-none opacity-40">HPG</span>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl w-full px-6 grid lg:grid-cols-12 items-center gap-8 pt-24 pb-16">
        {/* Text block */}
        <motion.div style={{ y: yText, opacity }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-gold mb-6"
          >
            Street Certified · Proprietary Grabba Blend
          </motion.p>

          <h1 className="font-display uppercase leading-[0.82] tracking-tight text-[17vw] sm:text-[14vw] lg:text-[9.5vw]">
            <span className="reveal-mask">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block text-white">
                Hot
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-fire">
                Packz
              </motion.span>
            </span>
            <span className="reveal-mask">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-outline-gold">
                Grabba
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-8 max-w-md text-muted-foreground text-base sm:text-lg leading-relaxed"
          >
            Bold flavor. Loud identity. Crushed grabba from a proprietary blend
            of hand-picked leaf strains. This is the flavor that defines Hot
            Packz Grabba.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-find-store"
              onClick={() => document.querySelector("#locator")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-blaze text-white font-heading uppercase text-sm tracking-wider py-4 px-8 hover:bg-gold hover:text-ink transition-colors duration-200"
            >
              Find a Store
            </button>
            <button
              data-testid="hero-contact"
              onClick={() => document.querySelector("#preorder")?.scrollIntoView({ behavior: "smooth" })}
              className="border border-surface-border text-white font-heading uppercase text-sm tracking-wider py-4 px-8 hover:border-gold hover:text-gold transition-colors duration-200"
            >
              Become a Retailer
            </button>
          </motion.div>
        </motion.div>

        {/* Mascot */}
        <motion.div
          style={{ y: yMascot }}
          initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 flex justify-center lg:justify-end"
        >
          <div className="relative">
            <div className="absolute -inset-6 ember-glow blur-2xl animate-ember" aria-hidden="true" />
            <img
              src={MASCOT}
              alt="Hot Packz Grabba mascot"
              className="relative w-64 sm:w-80 lg:w-[26rem] drop-shadow-[0_0_35px_rgba(255,42,0,0.4)]"
            />
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={go}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce text-blaze" />
      </motion.button>
    </section>
  );
}
